import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import {
  setSupportProductInfo,
  TravelCategory,
} from "@b2bportal/core-self-serve";
import { activitiesPostBookingApi } from "../../store";
import type { ActivityReservationPageContainerProps } from "./types";

export const useActivityReservationPageRoute = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  if (reservationId == null || reservationId === "") {
    throw new Error("Missing or empty reservationId parameter");
  }
  return { reservationId };
};

export const useActivityReservationPageContainer = ({
  reservationId,
  tripsRoute,
}: ActivityReservationPageContainerProps & { tripsRoute: string }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateToTrips = useCallback(() => {
    navigate(tripsRoute, { replace: true });
  }, [tripsRoute, navigate]);

  const {
    data: activityReservation,
    isLoading,
    isError,
  } = activitiesPostBookingApi.useFetchActivityReservationQuery({
    customerReservationId: reservationId,
  });

  useEffect(() => {
    if (isError) {
      dispatch(setSupportProductInfo());
      navigateToTrips();
      return;
    }

    dispatch(
      setSupportProductInfo({
        productId: reservationId,
        productType: TravelCategory.Activities,
      })
    );

    return () => {
      dispatch(setSupportProductInfo());
    };
  }, [isError, dispatch, navigateToTrips, reservationId]);

  return useMemo(
    () => ({
      context: {
        activityReservation,
        isLoading,
        isError,
      },
      navigateToTrips,
    }),
    [activityReservation, isLoading, isError, navigateToTrips]
  );
};
